import { Auth0Provider as Auth0ReactProvider, useAuth0 } from "@auth0/auth0-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { PropsWithChildren } from "react";
import { auth0Config, type Auth0Config } from "./authConfig";

// Keep the callback and logout URLs centralized because they must be allowlisted in Auth0.
export const DEFAULT_AUTH0_CALLBACK_PATH = "/auth0/callback";
export const DEFAULT_AUTH0_LOGOUT_PATH = "/auth0/logout";

const DEFAULT_AUTH0_SCOPE = "openid profile email";
const AUTH0_LOGOUT_FLAG_KEY = "auth0LogoutInProgress";

interface Auth0AuthorizationParams {
  redirect_uri: string;
  scope: string;
}

interface Auth0Actions {
  logout: (returnToPath?: string) => Promise<void>;
  isAuthenticated: boolean;
  isAvailable: boolean;
  isAuthConfigLoading: boolean;
  isLoggingOut: boolean;
  isLoading: boolean;
  sdkErrorMessage: string | null;
  startLogin: (returnTo: string) => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

interface Auth0ProviderProps {
  config?: Auth0Config;
}

const AUTH0_UNAVAILABLE_ACTIONS: Auth0Actions = {
  logout: () => Promise.resolve(),
  isAuthenticated: false,
  isAvailable: false,
  isAuthConfigLoading: false,
  isLoggingOut: false,
  isLoading: false,
  sdkErrorMessage: null,
  startLogin: () => Promise.resolve(),
  getIdToken: () => Promise.resolve(null),
};

const Auth0Context = createContext<Auth0Actions>(AUTH0_UNAVAILABLE_ACTIONS);

const readAuth0LogoutFlag = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.sessionStorage.getItem(AUTH0_LOGOUT_FLAG_KEY) === "true";
};

const setAuth0LogoutFlag = (): void => {
  if (typeof window !== "undefined") {
    window.sessionStorage.setItem(AUTH0_LOGOUT_FLAG_KEY, "true");
  }
};

const clearAuth0LogoutFlag = (): void => {
  if (typeof window !== "undefined") {
    window.sessionStorage.removeItem(AUTH0_LOGOUT_FLAG_KEY);
  }
};

function buildAuthorizationParams(): Auth0AuthorizationParams {
  return {
    redirect_uri: `${window.location.origin}${DEFAULT_AUTH0_CALLBACK_PATH}`,
    scope: DEFAULT_AUTH0_SCOPE,
  };
}

function resolveLogoutReturnTo(returnToPath?: string): string {
  if (typeof window === "undefined") {
    return "";
  }

  if (!returnToPath) {
    return `${window.location.origin}${DEFAULT_AUTH0_LOGOUT_PATH}`;
  }

  try {
    const returnToUrl = new URL(returnToPath, window.location.origin);
    return returnToUrl.origin === window.location.origin ? returnToUrl.toString() : window.location.origin;
  } catch {
    return window.location.origin;
  }
}

/**
 * Provides Auth0 actions and state for WorkUI login flows.
 */
function Auth0ContextProvider({ children }: PropsWithChildren<unknown>) {
  const { error, getIdTokenClaims, isAuthenticated, isLoading, loginWithRedirect, logout } = useAuth0();
  const [isLoggingOut, setIsLoggingOut] = useState(readAuth0LogoutFlag);

  const handleLogout = useCallback(
    async (returnToPath?: string) => {
      setAuth0LogoutFlag();
      setIsLoggingOut(true);

      try {
        await logout({ logoutParams: { returnTo: resolveLogoutReturnTo(returnToPath) } });
      } catch (error) {
        clearAuth0LogoutFlag();
        setIsLoggingOut(false);
        throw error;
      }
    },
    [logout],
  );

  // Clear the logout flag once Auth0 confirms the user is signed out.
  useEffect(() => {
    if (isLoggingOut && !isLoading && !isAuthenticated) {
      clearAuth0LogoutFlag();
      setIsLoggingOut(false);
    }
  }, [isAuthenticated, isLoading, isLoggingOut]);

  const getIdToken = useCallback(async () => {
    const claims = await getIdTokenClaims();
    return claims?.__raw ?? null;
  }, [getIdTokenClaims]);

  const startLogin = useCallback(
    async (returnTo: string) => {
      await loginWithRedirect({ appState: { returnTo } });
    },
    [loginWithRedirect],
  );

  const value = useMemo<Auth0Actions>(
    () => ({
      logout: handleLogout,
      isAuthenticated,
      isAvailable: true,
      isAuthConfigLoading: false,
      isLoggingOut,
      isLoading,
      sdkErrorMessage: error?.message ?? null,
      startLogin,
      getIdToken,
    }),
    [error?.message, getIdToken, handleLogout, isAuthenticated, isLoading, isLoggingOut, startLogin],
  );

  return <Auth0Context.Provider value={value}>{children}</Auth0Context.Provider>;
}

/**
 * Accessor for Auth0 UI state and actions (login, logout, and token claims).
 * Must be used within an Auth0Provider.
 *
 * @returns Auth0 actions and state:
 * - `logout` triggers a full Auth0 logout redirect.
 * - `isAuthenticated` indicates the Auth0 session is established.
 * - `isAvailable` indicates Auth0 is configured and the provider is active.
 * - `isAuthConfigLoading` is false for WorkUI's static configuration source.
 * - `isLoggingOut` is true while an Auth0 logout redirect is in-flight.
 * - `isLoading` reflects Auth0 SDK initialization/loading state.
 * - `sdkErrorMessage` contains the Auth0 SDK callback/session error message.
 * - `startLogin` starts the Auth0 redirect flow with an app return location.
 * - `getIdToken` returns the raw Auth0 ID token string, or null if unavailable.
 */
export function useAuth0Context(): Auth0Actions {
  return useContext(Auth0Context);
}

/**
 * Provides Auth0 SDK context and exposes WorkUI login/logout state and actions.
 *
 * Auth0 remains unavailable when the local configuration does not contain both
 * a domain and client ID, allowing the existing legacy login flow to continue.
 *
 * @param children React subtree that needs Auth0 access.
 * @param config Optional Auth0 configuration override for tests or embedding.
 * @returns A provider component that supplies Auth0 actions and state.
 */
export default function Auth0Provider({ children, config = auth0Config }: PropsWithChildren<Auth0ProviderProps>) {
  if (!config.domain || !config.clientId) {
    return <Auth0Context.Provider value={AUTH0_UNAVAILABLE_ACTIONS}>{children}</Auth0Context.Provider>;
  }

  return (
    <Auth0ReactProvider
      authorizationParams={buildAuthorizationParams()}
      clientId={config.clientId}
      domain={config.domain}
    >
      <Auth0ContextProvider>{children}</Auth0ContextProvider>
    </Auth0ReactProvider>
  );
}
